import { defineStore } from 'pinia';

import type { SeasonalThemesResponse, SeasonalThemeState, SeasonKey } from '~/support/services/identity.service';

import { useAuthStore } from './auth';
import { useToastStore } from './toast';

export enum Season {
  HALLOWEEN = 'halloween',
  THANKSGIVING = 'thanksgiving',
}

const SAVE_FAILED_TOAST_MS = 5000;

// Nothing is saved yet: the season is off for everyone until engineering turns it on, and on for the person.
const defaultState = (): SeasonalThemeState => ({ enabled: false, preference: { eggs: {}, enabled: true } });

/**
 * Server-side seasonal theme settings, shared by every season's store: engineering's switch that makes a season live
 * for everyone (off until turned on), and the
 * signed-in person's own on/off and easter-egg choices, which follow them to every device. Changes apply at once and
 * roll back with a toast if saving fails.
 */
export const useSeasonalPreferencesStore = defineStore('seasonalPreferences', () => {
  const state = ref<Partial<SeasonalThemesResponse>>({});
  const loaded = ref(false);
  let wanted = false;
  let requested = false;
  let generation = 0;
  // Switches flipped before the saved state arrived, re-applied on top of it field by field so nothing else is lost.
  const early = {
    eggs: {} as Partial<Record<SeasonKey, Record<string, boolean>>>,
    global: {} as Partial<Record<SeasonKey, boolean>>,
    personal: {} as Partial<Record<SeasonKey, boolean>>,
  };

  function season(key: SeasonKey): SeasonalThemeState {
    return state.value[key] ?? defaultState();
  }

  const globalEnabled = (key: SeasonKey) => season(key).enabled;
  const personalEnabled = (key: SeasonKey) => season(key).preference.enabled;
  const eggEnabled = (key: SeasonKey, egg: string) => season(key).preference.eggs[egg] ?? true;

  // Loads once per signed-in session; a call made while signed out runs as soon as the person signs in.
  function load() {
    wanted = true;
    if (requested || !useAuthStore().authenticated) return;
    requested = true;
    const current = generation;

    useGateway()
      .identity.seasonalThemes.get()
      .subscribe({
        error: () => {
          if (current === generation) loaded.value = true;
        },
        next: response => {
          if (current !== generation) return;
          if (response?.data) state.value = withEarlyChanges(response.data);
          loaded.value = true;
        },
      });
  }

  // Forgets the previous person's choices so the next sign-in in this tab starts clean.
  function reset() {
    generation += 1;
    requested = false;
    loaded.value = false;
    state.value = {};
    early.eggs = {};
    early.global = {};
    early.personal = {};
  }

  watch(
    () => useAuthStore().authenticated,
    authenticated => {
      if (!authenticated) return reset();
      if (wanted) load();
    },
    { flush: 'sync' },
  );

  function withEarlyChanges(saved: Partial<SeasonalThemesResponse>): Partial<SeasonalThemesResponse> {
    const merged = { ...saved };

    (Object.keys(merged) as SeasonKey[]).forEach(key => {
      const season = merged[key] ?? defaultState();

      merged[key] = {
        enabled: early.global[key] ?? season.enabled,
        preference: {
          eggs: { ...season.preference.eggs, ...early.eggs[key] },
          enabled: early.personal[key] ?? season.preference.enabled,
        },
      };
    });

    return merged;
  }

  function replace(key: SeasonKey, next: SeasonalThemeState) {
    state.value = { ...state.value, [key]: next };
  }

  // Undoes only the field whose save failed, leaving later successful changes alone, and tells the user.
  function rollback(undo: () => void) {
    return () => {
      undo();
      useToastStore().addToast({
        message: 'That theme setting did not save. Please try again.',
        timeout: SAVE_FAILED_TOAST_MS,
        type: 'error',
      });
    };
  }

  function writePersonal(key: SeasonKey, enabled: boolean) {
    const current = season(key);

    replace(key, { ...current, preference: { ...current.preference, enabled } });
  }

  function writeEgg(key: SeasonKey, egg: string, enabled: boolean | undefined) {
    const current = season(key);
    const eggs = { ...current.preference.eggs, [egg]: enabled ?? true };

    if (enabled === undefined) delete eggs[egg];
    replace(key, { ...current, preference: { ...current.preference, eggs } });
  }

  function writeGlobal(key: SeasonKey, enabled: boolean) {
    replace(key, { ...season(key), enabled });
  }

  function setPersonal(key: SeasonKey, enabled: boolean) {
    const previous = personalEnabled(key);
    const { identity } = useGateway();

    if (!loaded.value) early.personal[key] = enabled;
    writePersonal(key, enabled);
    identity.seasonalThemes.setPreference(key, { enabled }).subscribe({
      error: rollback(() => {
        if (early.personal[key] === enabled) delete early.personal[key];
        writePersonal(key, previous);
      }),
    });
  }

  // Sends only the changed egg; the server merges it into the saved map.
  function setEgg(key: SeasonKey, egg: string, enabled: boolean) {
    const previous = season(key).preference.eggs[egg];
    const { identity } = useGateway();

    if (!loaded.value) early.eggs[key] = { ...early.eggs[key], [egg]: enabled };
    writeEgg(key, egg, enabled);
    identity.seasonalThemes.setPreference(key, { eggs: { [egg]: enabled } }).subscribe({
      error: rollback(() => {
        if (early.eggs[key]?.[egg] === enabled) delete early.eggs[key]?.[egg];
        writeEgg(key, egg, previous);
      }),
    });
  }

  // Only one season is live at a time, so switching one on switches the others off, as the server does.
  function setGlobal(key: SeasonKey, enabled: boolean) {
    const others = enabled ? Object.values(Season).filter(other => other !== key && globalEnabled(other)) : [];
    const previous = globalEnabled(key);
    const { identity } = useGateway();
    const changed = [key, ...others];

    if (!loaded.value) {
      early.global[key] = enabled;
      others.forEach(other => (early.global[other] = false));
    }
    writeGlobal(key, enabled);
    others.forEach(other => writeGlobal(other, false));
    identity.seasonalThemes.setGlobal(key, enabled).subscribe({
      error: rollback(() => {
        changed.forEach(changedKey => delete early.global[changedKey]);
        writeGlobal(key, previous);
        others.forEach(other => writeGlobal(other, true));
      }),
    });
  }

  return { eggEnabled, globalEnabled, load, loaded, personalEnabled, setEgg, setGlobal, setPersonal, state };
});
